import React, { useState } from 'react';
import {
  Box,
  Alert,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AutoAwesome as AIIcon,
  MusicNote as MusicIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { safeDisplayValue } from '../../utils/arrayStringHelpers';

interface CompactAISuccessDisplayProps {
  description: string;
  tags: string[];
  processingType?: string;
  layerMetadata?: any;
  layer: string;
  onRegenerateClick?: () => void;
  isGenerating?: boolean;
}

/**
 * Compact AI Success Display Component
 *
 * A more aesthetically pleasing and space-efficient alternative to the verbose
 * AI success box. Features:
 * - Compact initial view with key information
 * - Expandable details section
 * - Smart tag display with "Show More" functionality
 * - Clean typography and spacing
 * - Layer-specific icons and information
 */
const CompactAISuccessDisplay: React.FC<CompactAISuccessDisplayProps> = ({
  description,
  tags = [],
  processingType = 'ai-contract',
  layerMetadata,
  layer,
  onRegenerateClick,
  isGenerating = false,
}) => {
  const [expanded, setExpanded] = useState(true); // Start expanded to show rich AI content

  // Show all tags by default as requested
  const displayTags = tags;

  // Layer-specific icon
  const getLayerIcon = () => {
    switch (layer) {
      case 'G':
        return <MusicIcon />;
      case 'S':
        return <StarIcon />;
      default:
        return <AIIcon />;
    }
  };

  // Extract key information based on layer
  const getKeyInfo = () => {
    if (layer === 'G' && layerMetadata?.layerType === 'songs') {
      return {
        title: layerMetadata.songName || 'Unknown Song',
        subtitle: layerMetadata.artistName || 'Unknown Artist',
        details: [
          layerMetadata.genre &&
            `Genre: ${safeDisplayValue(layerMetadata.genre)}`,
          layerMetadata.bpm && `BPM: ${layerMetadata.bpm}`,
          layerMetadata.key && `Key: ${layerMetadata.key}`,
        ].filter(Boolean),
      };
    }

    if (layer === 'S' && layerMetadata?.layerType === 'stars') {
      return {
        title:
          layerMetadata.celebrityName ||
          layerMetadata.starName ||
          'Star Profile',
        subtitle: layerMetadata.primaryStyle || 'Performance Artist',
        details: [
          layerMetadata.gender && `Gender: ${layerMetadata.gender}`,
          layerMetadata.apparentAgeRange &&
            `Age: ${layerMetadata.apparentAgeRange}`,
          layerMetadata.energyProfile &&
            `Energy: ${layerMetadata.energyProfile}`,
        ].filter(Boolean),
      };
    }

    return {
      title: `${layer} Layer Metadata`,
      subtitle: 'AI Generated Content',
      details: [],
    };
  };

  const keyInfo = getKeyInfo();

  // DEBUG: Log what data the component is receiving
  console.log(
    `🎯 [COMPACT AI SUCCESS] Rendering with ${tags?.length || 0} tags`
  );

  return (
    <Alert
      severity="success"
      sx={{
        mb: 2,
        borderRadius: 2,
        '& .MuiAlert-icon': {
          fontSize: 20,
        },
      }}
    >
      {/* Compact Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getLayerIcon()}
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold', color: 'success.dark' }}
          >
            {processingType === 'ai-contract' ? 'AI Contract' : 'AI'} Generated
            Successfully
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ color: 'success.dark' }}
        >
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
      </Box>

      {/* Key Information Row */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
          {keyInfo.title}
        </Typography>
        {keyInfo.subtitle && (
          <Typography variant="caption" color="text.secondary">
            {keyInfo.subtitle}
          </Typography>
        )}
        {keyInfo.details.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            {keyInfo.details.map((detail, index) => (
              <Typography
                key={index}
                variant="caption"
                color="text.secondary"
                sx={{ mr: 2 }}
              >
                {detail}
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      {/* Compact Description Preview */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1.5,
          fontStyle: 'italic',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 'none' : 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        "{description}"
      </Typography>

      {/* Compact Tags Display */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 'bold', color: 'text.primary' }}
          >
            Tags ({tags.length}):
          </Typography>
          <Chip
            label="Pre-filtering"
            size="small"
            variant="outlined"
            color="info"
            sx={{
              fontSize: '0.65rem',
              height: 20,
              '& .MuiChip-label': { px: 1 }
            }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5, fontStyle: 'italic' }}
        >
          Raw AI-generated tags (generic tags will be filtered during submission)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {displayTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                borderColor: 'success.light',
                color: 'success.dark',
                '&:hover': {
                  bgcolor: 'success.light',
                  opacity: 0.1,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Expandable Details */}
      <Collapse in={expanded}>
        <Box
          sx={{ pt: 1, borderTop: '1px solid', borderColor: 'success.light' }}
        >
          {/* Layer-Specific Detailed Information */}
          {layer === 'G' && layerMetadata?.layerType === 'songs' && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}
              >
                🎵 Detailed Song Information:
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1,
                  fontSize: '0.75rem',
                }}
              >
                {layerMetadata.albumName && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Album:</strong> {layerMetadata.albumName}
                  </Typography>
                )}
                {layerMetadata.releaseYear && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Year:</strong> {layerMetadata.releaseYear}
                  </Typography>
                )}
                {layerMetadata.duration && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Duration:</strong>{' '}
                    {Math.floor(layerMetadata.duration / 60)}:
                    {(layerMetadata.duration % 60).toString().padStart(2, '0')}
                  </Typography>
                )}
                {layerMetadata.tempo && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Tempo:</strong> {layerMetadata.tempo}
                  </Typography>
                )}
                {layerMetadata.mood && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Mood:</strong> {layerMetadata.mood}
                  </Typography>
                )}
                {layerMetadata.energy && (
                  <Typography variant="caption" color="text.secondary">
                    <strong>Energy:</strong> {layerMetadata.energy}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Processing Information */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Generated with{' '}
              {processingType === 'ai-contract' ? 'AI Contract' : 'Enhanced AI'}{' '}
              •{tags.length} tags • {Math.ceil(description.length / 5)} words
            </Typography>
            {onRegenerateClick && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={onRegenerateClick}
                disabled={isGenerating}
                sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
              >
                Generate Again
              </Button>
            )}
          </Box>
        </Box>
      </Collapse>
    </Alert>
  );
};

export default CompactAISuccessDisplay;
